import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../api";
import { SISTEMAS_EM_ORDEM, nomeDoSistema } from "../sistemas";
import {
  IconPlus,
  IconTrash,
  IconCheck,
  IconClose,
  IconCalendar,
  IconFolder,
  IconChevronLeft,
  IconChevronRight,
  IconCalendarCheck,
  IconClock,
  IconLock,
  IconEye,
  IconBell,
  IconInbox,
  IconDownload,
} from "../icons";
import ErrorState from "./ErrorState";
import { dataBr } from "./PainelView";
import {
  ativarNotificacao,
  desativarNotificacao,
  ehComputador,
  estadoNotificacao,
  instalarApp,
  podeInstalar,
  sincronizarNotificacao,
  type EstadoNotificacao,
  type SituacaoAparelho,
} from "../pwa";

/**
 * AGENDA — a semana do desenvolvimento, os pedidos de reunião e a agenda ABERTA.
 *
 *  • SEMANA (padrão) — os dias em colunas e as horas em linhas, como numa agenda
 *    de calendário. As faixas em que você está livre aparecem de leve ao fundo,
 *    os bloqueios riscados, e a linha "agora" corta o dia de hoje. Clicar num
 *    espaço vazio marca uma reunião naquele horário.
 *  • MÊS — a visão de longe, para achar um dia.
 *  • PAINEL AO LADO — o que pede ação: os PEDIDOS de reunião vindos dos
 *    sistemas (aceitar / recusar), o dia de hoje e as notificações do celular.
 *  • HORÁRIOS LIVRES E LINK — onde você diz quando pode ser procurado.
 *
 * Um pedido ('Solicitada') já ocupa o horário na grade, tracejado: ele está
 * reservado para quem pediu até você responder (ver server/pedidos-reuniao.ts).
 */

interface Reuniao {
  id: number;
  titulo: string;
  data: string;
  hora: string | null;
  duracao_min: number | null;
  local: string | null;
  link: string | null;
  participantes: string | null;
  pauta: string | null;
  sistema: string | null;
  status: string;
  origem: string | null;
  solicitante: string | null;
  email: string | null;
  telefone: string | null;
  ata: string | null;
  resposta: string | null;
  criado_em: string;
  passou: boolean;
}

const VAZIO = {
  titulo: "",
  data: "",
  hora: "",
  duracao_min: "",
  local: "",
  link: "",
  participantes: "",
  pauta: "",
  sistema: "",
};

const DIAS_SEMANA = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const DIAS_CURTOS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
/** Altura de uma hora na grade da semana (px). */
const PX_HORA = 56;
const MODO_KEY = "sercamp-dev-agenda-modo";

/** Motivos prontos para a recusa — a pessoa ainda pode escrever o dela. */
const MOTIVOS_RAPIDOS = [
  "Esse horário não serve mais — escolha outro, por favor.",
  "Dá para resolver por mensagem; vou te chamar no WhatsApp.",
  "O assunto já foi resolvido.",
];

/** "2026-09-04" (sem fuso: data pura, como o banco guarda). */
function iso(d: Date): string {
  return (
    d.getFullYear() +
    "-" +
    String(d.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(d.getDate()).padStart(2, "0")
  );
}

const deIso = (s: string) => {
  const [a, m, d] = s.slice(0, 10).split("-").map(Number);
  return new Date(a, m - 1, d);
};

const somaDias = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** "14:30" → 870. */
const emMin = (h: string | null | undefined): number | null => {
  const m = String(h ?? "").match(/^(\d{1,2}):(\d{2})/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const emHora = (min: number) =>
  String(Math.floor(min / 60)).padStart(2, "0") + ":" + String(min % 60).padStart(2, "0");

/** Segunda-feira da semana (a semana de trabalho começa na segunda). */
function segundaDa(d: Date): Date {
  const dia = d.getDay();
  return somaDias(d, dia === 0 ? -6 : 1 - dia);
}

/** As 6 semanas do mês, começando no domingo — a grade do mês. */
function gradeDoMes(referencia: Date): Date[] {
  const primeiro = new Date(referencia.getFullYear(), referencia.getMonth(), 1);
  const inicio = somaDias(primeiro, -primeiro.getDay());
  return Array.from({ length: 42 }, (_, i) => somaDias(inicio, i));
}

const iniciais = (nome: string | null) =>
  (nome ?? "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("") || "?";

/** "há 5 min", "há 2 h", "há 3 dias". */
function haQuanto(quando: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(quando).getTime()) / 60000));
  if (min < 1) return "agora";
  if (min < 60) return "há " + min + " min";
  const h = Math.round(min / 60);
  if (h < 24) return "há " + h + " h";
  const d = Math.round(h / 24);
  return "há " + d + (d === 1 ? " dia" : " dias");
}

const diaCurto = (dataIso: string) => {
  const d = deIso(dataIso);
  return DIAS_CURTOS[d.getDay()] + ", " + dataBr(dataIso).slice(0, 5);
};

function lerModo(): "semana" | "mes" | "livres" {
  try {
    const v = localStorage.getItem(MODO_KEY);
    return v === "mes" || v === "livres" ? v : "semana";
  } catch {
    return "semana";
  }
}

/**
 * Distribui as reuniões que se sobrepõem lado a lado (em "raias"), como faz
 * qualquer agenda: sem isso, dois blocos no mesmo horário ficariam um em cima do
 * outro e o de baixo sumiria.
 */
function emRaias<T extends { ini: number; fim: number }>(itens: T[]) {
  const ordenados = [...itens].sort((a, b) => a.ini - b.ini || b.fim - a.fim);
  const saida: (T & { raia: number; raias: number })[] = [];
  let grupo: (T & { raia: number; raias: number })[] = [];
  let fimGrupo = -1;
  const fecha = () => {
    const total = Math.max(1, ...grupo.map((g) => g.raia + 1));
    for (const g of grupo) g.raias = total;
    saida.push(...grupo);
    grupo = [];
  };
  for (const it of ordenados) {
    if (grupo.length && it.ini >= fimGrupo) fecha();
    const ocupadas = grupo.filter((g) => g.fim > it.ini).map((g) => g.raia);
    let raia = 0;
    while (ocupadas.includes(raia)) raia++;
    grupo.push({ ...it, raia, raias: 1 });
    fimGrupo = Math.max(fimGrupo, it.fim);
  }
  if (grupo.length) fecha();
  return saida;
}

export default function AgendaView({
  onMudou,
  versao = 0,
  somenteLeitura = false,
}: {
  onMudou: () => void;
  versao?: number;
  /** Somente visualização (Portal): trava a configuração dos horários livres. */
  somenteLeitura?: boolean;
}) {
  const [modo, setModoBruto] = useState(lerModo);
  const [linhas, setLinhas] = useState<Reuniao[]>([]);
  const [agenda, setAgenda] = useState<any | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState<any | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [referencia, setReferencia] = useState(() => new Date());
  const [recusa, setRecusa] = useState<{ r: Reuniao; motivo: string } | null>(null);
  const [agora, setAgora] = useState(() => new Date());

  const setModo = (m: "semana" | "mes" | "livres") => {
    setModoBruto(m);
    try {
      localStorage.setItem(MODO_KEY, m);
    } catch {
      /* aba anônima — só não lembra da escolha */
    }
  };

  const carregar = useCallback(async () => {
    try {
      const [rs, cfg] = await Promise.all([
        api.reunioes.listar() as Promise<Reuniao[]>,
        api.agenda.config().catch(() => null),
      ]);
      setLinhas(rs);
      setAgenda(cfg);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível carregar a agenda.");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar, versao]);

  // O relógio da linha "agora" (e o "há 5 min" dos pedidos).
  useEffect(() => {
    const id = setInterval(() => setAgora(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const duracaoPadrao: number = agenda?.config?.duracao_min ?? 30;
  const hoje = iso(agora);

  const porDia = useMemo(() => {
    const mapa = new Map<string, Reuniao[]>();
    for (const r of linhas) {
      if (r.status === "Recusada") continue; // recusada libera o horário: some da grade
      const dia = String(r.data).slice(0, 10);
      const lista = mapa.get(dia) ?? [];
      lista.push(r);
      mapa.set(dia, lista);
    }
    for (const lista of mapa.values()) {
      lista.sort((a, b) => String(a.hora ?? "").localeCompare(String(b.hora ?? "")));
    }
    return mapa;
  }, [linhas]);

  const pedidos = useMemo(
    () =>
      linhas
        .filter((r) => r.status === "Solicitada")
        .sort((a, b) => (a.data + (a.hora ?? "")).localeCompare(b.data + (b.hora ?? ""))),
    [linhas],
  );

  async function salvar() {
    if (!form?.titulo?.trim() || !form?.data) {
      alert("Informe o assunto e o dia da reunião.");
      return;
    }
    setSalvando(true);
    try {
      if (form.id) await api.reunioes.atualizar(form.id, form);
      else await api.reunioes.criar(form);
      setForm(null);
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  }

  async function mudarStatus(r: Reuniao, status: string) {
    try {
      await api.reunioes.atualizar(r.id, { status });
      setForm(null);
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao atualizar.");
    }
  }

  async function excluir(r: Reuniao) {
    if (!confirm("Apagar a reunião " + r.titulo + "?")) return;
    try {
      await api.reunioes.remover(r.id);
      setForm(null);
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Erro ao excluir.");
    }
  }

  /** Aceitar. Vindo do modal, grava antes o que foi ajustado (local, link…). */
  async function aceitar(r: Reuniao, ajustes?: Record<string, unknown>) {
    setSalvando(true);
    try {
      if (ajustes) {
        const { status: _s, ...resto } = ajustes;
        await api.reunioes.atualizar(r.id, resto);
      }
      await api.reunioes.aceitar(r.id);
      setForm(null);
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Não foi possível aceitar.");
    } finally {
      setSalvando(false);
    }
  }

  async function confirmarRecusa() {
    if (!recusa) return;
    if (!recusa.motivo.trim()) {
      alert("Escreva o motivo — é o que a pessoa vai ler.");
      return;
    }
    setSalvando(true);
    try {
      await api.reunioes.recusar(recusa.r.id, recusa.motivo.trim());
      setRecusa(null);
      setForm(null);
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Não foi possível recusar.");
    } finally {
      setSalvando(false);
    }
  }

  const abrir = (r: Reuniao) =>
    setForm({
      ...r,
      data: String(r.data).slice(0, 10),
      hora: r.hora ?? "",
      duracao_min: r.duracao_min ?? "",
    });

  const novaEm = (dia: string, hora = "") => setForm({ ...VAZIO, data: dia, hora });

  const set = (campo: string) => (e: any) => setForm({ ...form, [campo]: e.target.value });

  /* ---------------- navegação ---------------- */
  const segunda = segundaDa(referencia);
  const andar = (n: number) =>
    setReferencia(
      modo === "mes"
        ? new Date(referencia.getFullYear(), referencia.getMonth() + n, 1)
        : somaDias(referencia, 7 * n),
    );
  const tituloPeriodo =
    modo === "mes"
      ? referencia.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
      : (() => {
          const fim = somaDias(segunda, 6);
          const mesmoMes = fim.getMonth() === segunda.getMonth();
          return (
            segunda.getDate() +
            (mesmoMes ? "" : " de " + segunda.toLocaleDateString("pt-BR", { month: "short" })) +
            " – " +
            fim.toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" })
          );
        })();

  const hojeLista = (porDia.get(hoje) ?? []).filter((r) => r.status !== "Cancelada");
  const semanaDias = Array.from({ length: 7 }, (_, i) => iso(somaDias(segundaDa(agora), i)));
  const naSemana = semanaDias.flatMap((d) => porDia.get(d) ?? []).filter((r) => r.status === "Marcada");
  const livresSemana = (agenda?.proximosLivres ?? [])
    .filter((d: any) => semanaDias.includes(d.data))
    .reduce((s: number, d: any) => s + d.horarios.length, 0);

  return (
    <div className="ag">
      {/* -------- BARRA -------- */}
      <div className="ag-barra">
        <div className="ag-nav">
          {modo !== "livres" && (
            <>
              <button className="btn-secondary btn-sm ag-hoje" onClick={() => setReferencia(new Date())}>
                Hoje
              </button>
              <button className="icon-btn" title="Anterior" onClick={() => andar(-1)}>
                <IconChevronLeft size={18} />
              </button>
              <button className="icon-btn" title="Próxima" onClick={() => andar(1)}>
                <IconChevronRight size={18} />
              </button>
              <strong className="ag-periodo">{tituloPeriodo}</strong>
            </>
          )}
        </div>
        <div className="ag-barra-dir">
          <div className="ag-seg" role="tablist">
            <button className={modo === "semana" ? "ativo" : ""} onClick={() => setModo("semana")}>
              Semana
            </button>
            <button className={modo === "mes" ? "ativo" : ""} onClick={() => setModo("mes")}>
              Mês
            </button>
            <button className={modo === "livres" ? "ativo" : ""} onClick={() => setModo("livres")}>
              Horários livres
            </button>
          </div>
          {modo !== "livres" && !somenteLeitura && (
            <button className="btn-primary" onClick={() => novaEm(hoje)}>
              <IconPlus size={16} /> Marcar reunião
            </button>
          )}
        </div>
      </div>

      {modo === "livres" ? (
        <HorariosLivres onMudou={carregar} somenteLeitura={somenteLeitura} />
      ) : erro ? (
        <ErrorState message={erro} onRetry={carregar} />
      ) : (
        <div className="ag-corpo">
          <div className="ag-principal">
            {carregando ? (
              <div className="carregando">Carregando…</div>
            ) : modo === "semana" ? (
              <GradeSemana
                segunda={segunda}
                porDia={porDia}
                agenda={agenda}
                agora={agora}
                duracaoPadrao={duracaoPadrao}
                onAbrir={abrir}
                onNova={novaEm}
              />
            ) : (
              <GradeMes
                referencia={referencia}
                porDia={porDia}
                hoje={hoje}
                onAbrir={abrir}
                onDia={(d) => {
                  setReferencia(deIso(d));
                  setModo("semana");
                }}
              />
            )}
          </div>

          {/* -------- PAINEL AO LADO -------- */}
          <aside className="ag-lado">
            <section className="ag-card">
              <h3 className="ag-card-tit">
                <IconInbox size={16} /> Pedidos de reunião
                {pedidos.length > 0 && <span className="ag-contagem">{pedidos.length}</span>}
              </h3>
              {pedidos.length === 0 ? (
                <p className="ag-vazio">
                  Nenhum pedido esperando. Quando alguém pedir um horário pelo sistema dele, ele
                  aparece aqui.
                </p>
              ) : (
                <ul className="ag-pedidos">
                  {pedidos.map((p) => (
                    <li key={p.id} className="ag-pedido">
                      <div className="ag-pedido-quem">
                        <span className="ag-avatar">{iniciais(p.solicitante)}</span>
                        <div>
                          <strong>{p.solicitante ?? "Sem nome"}</strong>
                          <small>
                            {p.sistema ? nomeDoSistema(p.sistema) : "Link de agendamento"} ·{" "}
                            {haQuanto(p.criado_em)}
                          </small>
                        </div>
                      </div>
                      <button
                        className="ag-pedido-quando"
                        title="Ver na semana"
                        onClick={() => {
                          setReferencia(deIso(p.data));
                          setModo("semana");
                        }}
                      >
                        <IconCalendar size={14} />
                        {diaCurto(p.data)} · {p.hora ?? "sem hora"}
                        {p.duracao_min ? " · " + p.duracao_min + " min" : ""}
                      </button>
                      <p className="ag-pedido-assunto" onClick={() => abrir(p)}>
                        {p.titulo}
                      </p>
                      {p.pauta && <p className="ag-pedido-pauta">{p.pauta}</p>}
                      <div className="ag-pedido-acoes">
                        <button
                          className="btn-secondary btn-sm"
                          disabled={salvando}
                          onClick={() => setRecusa({ r: p, motivo: "" })}
                        >
                          Recusar
                        </button>
                        <button className="btn-primary btn-sm" disabled={salvando} onClick={() => aceitar(p)}>
                          <IconCheck size={14} /> Aceitar
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="ag-card">
              <h3 className="ag-card-tit">
                <IconClock size={16} /> Hoje
                <span className="ag-card-sub">
                  {agora.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "short" })}
                </span>
              </h3>
              {hojeLista.length === 0 ? (
                <p className="ag-vazio">Nada marcado para hoje.</p>
              ) : (
                <ul className="ag-hoje-lista">
                  {hojeLista.map((r) => {
                    const ini = emMin(r.hora);
                    const fim = ini !== null ? ini + (r.duracao_min ?? duracaoPadrao) : null;
                    const agoraMin = agora.getHours() * 60 + agora.getMinutes();
                    const acontecendo = ini !== null && fim !== null && agoraMin >= ini && agoraMin < fim;
                    const acabou = fim !== null && agoraMin >= fim;
                    return (
                      <li
                        key={r.id}
                        className={
                          (acontecendo ? "agora " : "") +
                          (acabou || r.status === "Realizada" ? "feita " : "") +
                          (r.status === "Solicitada" ? "pendente" : "")
                        }
                        onClick={() => abrir(r)}
                      >
                        <span className="ag-hoje-hora">{r.hora ?? "—"}</span>
                        <span className="ag-hoje-txt">
                          <strong>{r.titulo}</strong>
                          <small>
                            {acontecendo
                              ? "acontecendo agora"
                              : r.status === "Solicitada"
                                ? "aguardando sua resposta"
                                : ini !== null && !acabou
                                  ? "em " + tempoAte(ini - agoraMin)
                                  : [r.solicitante, r.sistema && nomeDoSistema(r.sistema)]
                                      .filter(Boolean)
                                      .join(" · ") || r.status}
                          </small>
                        </span>
                        {r.link && acontecendo && (
                          <a
                            className="btn-primary btn-sm"
                            href={r.link}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Entrar
                          </a>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="ag-card">
              <h3 className="ag-card-tit">
                <IconCalendarCheck size={16} /> Esta semana
              </h3>
              <div className="ag-numeros">
                <div>
                  <strong>{naSemana.length}</strong>
                  <span>{naSemana.length === 1 ? "reunião marcada" : "reuniões marcadas"}</span>
                </div>
                <div>
                  <strong>{livresSemana}</strong>
                  <span>{livresSemana === 1 ? "horário livre" : "horários livres"}</span>
                </div>
              </div>
              {agenda && !agenda.config?.ativa && (
                <p className="ag-aviso">A agenda está fechada: ninguém consegue pedir horário agora.</p>
              )}
              <button className="btn-link" onClick={() => setModo("livres")}>
                Ajustar horários livres e link
              </button>
            </section>

            <CartaoNotificacoes />
          </aside>
        </div>
      )}

      {/* -------- RECUSAR (modal) -------- */}
      {recusa && (
        <div className="modal-overlay" onMouseDown={() => setRecusa(null)}>
          <div className="modal-card ag-modal-estreito" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h3>Recusar pedido</h3>
                <p className="modal-sub">
                  {recusa.r.solicitante} · {diaCurto(recusa.r.data)} às {recusa.r.hora}. O motivo
                  aparece para a pessoa no sistema dela.
                </p>
              </div>
              <button className="icon-btn" onClick={() => setRecusa(null)}>
                <IconClose size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="ag-motivos">
                {MOTIVOS_RAPIDOS.map((m) => (
                  <button
                    key={m}
                    className={"ag-motivo" + (recusa.motivo === m ? " ativo" : "")}
                    onClick={() => setRecusa({ ...recusa, motivo: m })}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <label className="field field-wide">
                <span>
                  Motivo <span className="req">*</span>
                </span>
                <textarea
                  rows={3}
                  autoFocus
                  value={recusa.motivo}
                  onChange={(e) => setRecusa({ ...recusa, motivo: e.target.value })}
                />
              </label>
            </div>
            <div className="modal-foot">
              <button className="btn-secondary" onClick={() => setRecusa(null)} disabled={salvando}>
                Voltar
              </button>
              <button className="btn-primary" onClick={confirmarRecusa} disabled={salvando}>
                {salvando ? "Enviando…" : "Recusar pedido"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -------- A REUNIÃO (modal) -------- */}
      {form && (
        <div className="modal-overlay" onMouseDown={() => setForm(null)}>
          <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <h3>
                  {!form.id
                    ? "Marcar reunião"
                    : form.status === "Solicitada"
                      ? "Pedido de reunião"
                      : "Reunião"}
                  {form.id && <span className={"ag-status ag-status-" + statusCss(form.status)}>{rotuloStatus(form.status)}</span>}
                </h3>
                <p className="modal-sub">
                  {form.status === "Solicitada"
                    ? "Pedida por " +
                      (form.solicitante ?? "—") +
                      (form.sistema ? ", do sistema " + nomeDoSistema(form.sistema) : " pelo link") +
                      ", " +
                      haQuanto(form.criado_em) +
                      ". Ajuste o local ou o link antes de aceitar, se precisar."
                    : form.id && form.origem !== "Interna" && form.solicitante
                      ? "Pedida por " +
                        form.solicitante +
                        (form.sistema ? " (" + nomeDoSistema(form.sistema) + ")" : "") +
                        " — quem pediu vê as mudanças no sistema dela."
                      : form.id
                        ? "Mudou o dia ou a hora? O lembrete acompanha."
                        : "Ao salvar, um lembrete do dia é criado automaticamente."}
                </p>
              </div>
              <button className="icon-btn" onClick={() => setForm(null)}>
                <IconClose size={18} />
              </button>
            </div>
            <div className="modal-body">
              {form.resposta && form.status !== "Solicitada" && (
                <p className="ag-resposta">
                  <strong>Resposta enviada:</strong> {form.resposta}
                </p>
              )}
              <div className="form-grid">
                <label className="field field-wide">
                  <span>
                    Assunto <span className="req">*</span>
                  </span>
                  <input value={form.titulo} onChange={set("titulo")} autoFocus={!form.id} />
                </label>
                <label className="field">
                  <span>
                    Dia <span className="req">*</span>
                  </span>
                  <input type="date" value={form.data ?? ""} onChange={set("data")} />
                </label>
                <label className="field">
                  <span>Hora</span>
                  <input type="time" value={form.hora ?? ""} onChange={set("hora")} />
                </label>
                <label className="field">
                  <span>Duração (min)</span>
                  <input
                    type="number"
                    min={5}
                    step={5}
                    value={form.duracao_min ?? ""}
                    placeholder={String(duracaoPadrao)}
                    onChange={set("duracao_min")}
                  />
                </label>
                <label className="field">
                  <span>Sistema</span>
                  <select value={form.sistema ?? ""} onChange={set("sistema")}>
                    <option value="">Geral</option>
                    {SISTEMAS_EM_ORDEM.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nome}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Local</span>
                  <input value={form.local ?? ""} onChange={set("local")} />
                </label>
                <label className="field field-wide">
                  <span>Link da chamada</span>
                  <input value={form.link ?? ""} onChange={set("link")} placeholder="https://…" />
                </label>
                <label className="field field-wide">
                  <span>Participantes</span>
                  <input value={form.participantes ?? ""} onChange={set("participantes")} />
                </label>
                {(form.email || form.telefone) && (
                  <p className="field-wide ag-contato">
                    Contato: {[form.email, form.telefone].filter(Boolean).join(" · ")}
                  </p>
                )}
                <label className="field field-wide">
                  <span>Pauta</span>
                  <textarea rows={3} value={form.pauta ?? ""} onChange={set("pauta")} />
                </label>
                {form.id && form.status !== "Solicitada" && (
                  <label className="field field-wide">
                    <span>Ata / o que ficou combinado</span>
                    <textarea rows={3} value={form.ata ?? ""} onChange={set("ata")} />
                  </label>
                )}
              </div>
            </div>
            {/* Somente visualização: a reunião abre só para ler — "Realizada",
                "Cancelar reunião" e "Marcar reunião" gravam e escapariam do
                esconde-por-texto. Fecha-se pelo X do cabeçalho. */}
            {!somenteLeitura && <div className="modal-foot ag-modal-foot">
              {form.id && (
                <button className="btn-link btn-danger-text ag-excluir" onClick={() => excluir(form)} disabled={salvando}>
                  <IconTrash size={14} /> Excluir
                </button>
              )}
              {form.status === "Solicitada" ? (
                <>
                  <button
                    className="btn-secondary"
                    disabled={salvando}
                    onClick={() => setRecusa({ r: form, motivo: "" })}
                  >
                    Recusar
                  </button>
                  <button className="btn-primary" disabled={salvando} onClick={() => aceitar(form, form)}>
                    <IconCheck size={15} /> {salvando ? "Aceitando…" : "Aceitar pedido"}
                  </button>
                </>
              ) : (
                <>
                  {form.id && form.status === "Marcada" && (
                    <>
                      <button className="btn-secondary" disabled={salvando} onClick={() => mudarStatus(form, "Cancelada")}>
                        Cancelar reunião
                      </button>
                      <button className="btn-secondary" disabled={salvando} onClick={() => mudarStatus(form, "Realizada")}>
                        <IconCheck size={15} /> Realizada
                      </button>
                    </>
                  )}
                  <button className="btn-primary" onClick={salvar} disabled={salvando}>
                    {salvando ? "Salvando…" : form.id ? "Salvar" : "Marcar reunião"}
                  </button>
                </>
              )}
            </div>}
          </div>
        </div>
      )}
    </div>
  );
}

const tempoAte = (min: number) =>
  min < 60 ? min + " min" : Math.floor(min / 60) + " h" + (min % 60 ? " " + (min % 60) + " min" : "");

const statusCss = (s: string) =>
  s === "Solicitada" ? "pendente" : s === "Marcada" ? "ok" : s === "Recusada" ? "recusada" : "neutro";
const rotuloStatus = (s: string) => (s === "Solicitada" ? "aguardando resposta" : s.toLowerCase());

/* ==================== A SEMANA ==================== */

function GradeSemana({
  segunda,
  porDia,
  agenda,
  agora,
  duracaoPadrao,
  onAbrir,
  onNova,
}: {
  segunda: Date;
  porDia: Map<string, Reuniao[]>;
  agenda: any | null;
  agora: Date;
  duracaoPadrao: number;
  onAbrir: (r: Reuniao) => void;
  onNova: (dia: string, hora?: string) => void;
}) {
  const faixas: { dia_semana: number; hora_inicio: string; hora_fim: string }[] =
    agenda?.disponibilidade ?? [];
  const bloqueios: { data: string; hora_inicio: string | null; hora_fim: string | null; motivo: string | null }[] =
    agenda?.bloqueios ?? [];
  const hoje = iso(agora);

  /* Sábado e domingo só aparecem quando têm alguma coisa (reunião ou horário
     livre) — senão são duas colunas vazias roubando espaço da semana. */
  const todos = Array.from({ length: 7 }, (_, i) => somaDias(segunda, i));
  const dias = todos.filter((d) => {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) return true;
    return (porDia.get(iso(d)) ?? []).length > 0 || faixas.some((f) => f.dia_semana === dow);
  });

  /* A faixa de horas mostrada: das 8h às 18h, esticando para caber o que existe. */
  let ini = 8 * 60;
  let fim = 18 * 60;
  for (const f of faixas) {
    ini = Math.min(ini, emMin(f.hora_inicio) ?? ini);
    fim = Math.max(fim, emMin(f.hora_fim) ?? fim);
  }
  for (const d of dias) {
    for (const r of porDia.get(iso(d)) ?? []) {
      const m = emMin(r.hora);
      if (m === null) continue;
      ini = Math.min(ini, m);
      fim = Math.max(fim, m + (r.duracao_min ?? duracaoPadrao));
    }
  }
  const hIni = Math.floor(ini / 60);
  const hFim = Math.min(24, Math.ceil(fim / 60));
  const horas = Array.from({ length: hFim - hIni }, (_, i) => hIni + i);
  const altura = horas.length * PX_HORA;
  const y = (min: number) => ((min - hIni * 60) / 60) * PX_HORA;
  const agoraMin = agora.getHours() * 60 + agora.getMinutes();

  const colunas = { gridTemplateColumns: `52px repeat(${dias.length}, minmax(112px, 1fr))` };

  /* No celular a semana não cabe: a grade rola de lado. Ao abrir (ou trocar de
     semana), ela vai sozinha até o dia de hoje — senão quinta e sexta ficariam
     escondidas atrás da segunda-feira. */
  const rolagem = useRef<HTMLDivElement>(null);
  const chaveSemana = iso(segunda);
  useEffect(() => {
    const caixa = rolagem.current;
    if (!caixa || caixa.scrollWidth <= caixa.clientWidth) return;
    const col = caixa.querySelector<HTMLElement>(".ag-coluna.hoje");
    caixa.scrollLeft = col ? Math.max(0, col.offsetLeft - 52 - 8) : 0;
  }, [chaveSemana]);

  return (
    <div className="ag-semana">
      <div className="ag-semana-rolagem" ref={rolagem}>
        <div className="ag-semana-cab" style={colunas}>
          <span />
          {dias.map((d) => {
            const dia = iso(d);
            const doDia = (porDia.get(dia) ?? []).filter((r) => r.status !== "Cancelada");
            return (
              <div key={dia} className={"ag-dia-cab" + (dia === hoje ? " hoje" : "") + (dia < hoje ? " passou" : "")}>
                <span className="ag-dia-nome">{DIAS_CURTOS[d.getDay()]}</span>
                <span className="ag-dia-num">{d.getDate()}</span>
                {doDia.length > 0 && <span className="ag-dia-qtd">{doDia.length}</span>}
              </div>
            );
          })}
        </div>

        {/* Reunião sem hora não cabe na grade: fica numa faixa própria no topo. */}
        {dias.some((d) => (porDia.get(iso(d)) ?? []).some((r) => !r.hora)) && (
          <div className="ag-semana-cab ag-dia-todo" style={colunas}>
            <span className="ag-hora-rot">dia</span>
            {dias.map((d) => (
              <div key={iso(d)}>
                {(porDia.get(iso(d)) ?? [])
                  .filter((r) => !r.hora)
                  .map((r) => (
                    <button key={r.id} className={"ag-chip st-" + statusCss(r.status)} onClick={() => onAbrir(r)}>
                      {r.titulo}
                    </button>
                  ))}
              </div>
            ))}
          </div>
        )}

        <div className="ag-semana-grade" style={colunas}>
          <div className="ag-horas" style={{ height: altura }}>
            {horas.map((h) => (
              <span key={h} className="ag-hora-rot" style={{ top: y(h * 60) }}>
                {String(h).padStart(2, "0")}h
              </span>
            ))}
          </div>

          {dias.map((d) => {
            const dia = iso(d);
            const dow = d.getDay();
            const bloqDia = bloqueios.filter((b) => b.data === dia);
            const diaTodoBloqueado = bloqDia.some((b) => !b.hora_inicio || !b.hora_fim);
            const eventos = emRaias(
              (porDia.get(dia) ?? [])
                .filter((r) => r.hora)
                .map((r) => {
                  const a = emMin(r.hora) ?? 0;
                  return { r, ini: a, fim: a + Math.max(15, r.duracao_min ?? duracaoPadrao) };
                }),
            );
            return (
              <div
                key={dia}
                className={"ag-coluna" + (dia === hoje ? " hoje" : "") + (dia < hoje ? " passou" : "")}
                style={{ height: altura, backgroundSize: `100% ${PX_HORA}px` }}
                title={"Marcar reunião em " + dataBr(dia)}
                onClick={(e) => {
                  const topo = (e.currentTarget as HTMLElement).getBoundingClientRect().top;
                  const min = hIni * 60 + ((e.clientY - topo) / PX_HORA) * 60;
                  const arred = Math.max(0, Math.min(23 * 60 + 30, Math.floor(min / 30) * 30));
                  onNova(dia, emHora(arred));
                }}
              >
                {/* quando você está livre (de leve, ao fundo) */}
                {!diaTodoBloqueado &&
                  faixas
                    .filter((f) => f.dia_semana === dow)
                    .map((f, i) => {
                      const a = emMin(f.hora_inicio) ?? 0;
                      const b = emMin(f.hora_fim) ?? 0;
                      return <div key={i} className="ag-livre" style={{ top: y(a), height: y(b) - y(a) }} />;
                    })}
                {/* bloqueios (riscados) */}
                {bloqDia.map((b, i) => {
                  const a = b.hora_inicio && b.hora_fim ? emMin(b.hora_inicio) ?? 0 : hIni * 60;
                  const z = b.hora_inicio && b.hora_fim ? emMin(b.hora_fim) ?? 0 : hFim * 60;
                  return (
                    <div key={"b" + i} className="ag-bloq" style={{ top: y(a), height: y(z) - y(a) }}>
                      <span>{b.motivo || "bloqueado"}</span>
                    </div>
                  );
                })}

                {eventos.map(({ r, ini: a, fim: z, raia, raias }) => {
                  const altura = Math.max(22, y(z) - y(a) - 2);
                  // Bloco baixo (até ~45 min): hora e assunto na MESMA linha,
                  // senão o assunto fica escondido embaixo da hora.
                  const curto = altura < 50;
                  const quem = [r.solicitante, r.sistema && nomeDoSistema(r.sistema)].filter(Boolean).join(" · ");
                  return (
                    <button
                      key={r.id}
                      className={
                        "ag-ev st-" +
                        statusCss(r.status) +
                        (curto ? " curto" : "") +
                        (r.status === "Cancelada" ? " cancelada" : "") +
                        (r.status === "Realizada" ? " realizada" : "")
                      }
                      style={{
                        top: y(a) + 1,
                        height: altura,
                        left: `calc(${(raia / raias) * 100}% + 2px)`,
                        width: `calc(${100 / raias}% - 4px)`,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAbrir(r);
                      }}
                      title={
                        emHora(a) + "–" + emHora(z) + " · " + r.titulo +
                        (quem ? "\n" + quem : "") +
                        (r.status === "Solicitada" ? "\nAguardando a sua resposta" : "")
                      }
                    >
                      {curto ? (
                        <>
                          <span className="ag-ev-hora">{emHora(a)}</span>
                          <span className="ag-ev-tit">{r.titulo}</span>
                        </>
                      ) : (
                        <>
                          <span className="ag-ev-hora">
                            {emHora(a)}–{emHora(z)}
                            {r.status === "Solicitada" && <em> · aguardando</em>}
                          </span>
                          <span className="ag-ev-tit">{r.titulo}</span>
                          {altura >= 70 && quem && <span className="ag-ev-sub">{quem}</span>}
                        </>
                      )}
                    </button>
                  );
                })}

                {dia === hoje && agoraMin >= hIni * 60 && agoraMin <= hFim * 60 && (
                  <div className="ag-agora" style={{ top: y(agoraMin) }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <p className="ag-legenda">
        <span>
          <i className="ag-leg ag-leg-livre" /> livre para pedidos
        </span>
        <span>
          <i className="ag-leg ag-leg-ok" /> marcada
        </span>
        <span>
          <i className="ag-leg ag-leg-pend" /> aguardando resposta
        </span>
        <span>
          <i className="ag-leg ag-leg-bloq" /> bloqueado
        </span>
      </p>
    </div>
  );
}

/* ==================== O MÊS ==================== */

function GradeMes({
  referencia,
  porDia,
  hoje,
  onAbrir,
  onDia,
}: {
  referencia: Date;
  porDia: Map<string, Reuniao[]>;
  hoje: string;
  onAbrir: (r: Reuniao) => void;
  onDia: (dia: string) => void;
}) {
  const grade = gradeDoMes(referencia);
  return (
    <div className="dev-cal ag-mes">
      {DIAS_CURTOS.map((d) => (
        <div className="dev-cal-cab" key={d}>
          {d}
        </div>
      ))}
      {grade.map((d) => {
        const dia = iso(d);
        const doDia = porDia.get(dia) ?? [];
        const outroMes = d.getMonth() !== referencia.getMonth();
        return (
          <button
            type="button"
            key={dia}
            className={"dev-cal-dia" + (outroMes ? " fora" : "") + (dia === hoje ? " hoje" : "")}
            title={"Ver a semana de " + dataBr(dia)}
            onClick={() => onDia(dia)}
          >
            <span className="dev-cal-num">{d.getDate()}</span>
            {doDia.slice(0, 3).map((r) => (
              <span
                key={r.id}
                className={
                  "dev-cal-item st-" +
                  statusCss(r.status) +
                  (r.status === "Cancelada" ? " cancelada" : r.status === "Realizada" ? " realizada" : "")
                }
                onClick={(e) => {
                  e.stopPropagation();
                  onAbrir(r);
                }}
              >
                {r.hora ? r.hora + " " : ""}
                {r.titulo}
              </span>
            ))}
            {doDia.length > 3 && <span className="dev-cal-mais">+{doDia.length - 3}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ==================== NOTIFICAÇÕES NO CELULAR ==================== */

function CartaoNotificacoes() {
  const [estado, setEstado] = useState<EstadoNotificacao | null>(null);
  /** O que o SERVIDOR sabe deste aparelho; null = não deu para confirmar. */
  const [situacao, setSituacao] = useState<SituacaoAparelho | null | undefined>(undefined);
  const [ocupado, setOcupado] = useState(false);
  const [instalavel, setInstalavel] = useState(podeInstalar);
  const [msg, setMsg] = useState<string | null>(null);

  /* "Ligado" só quando o servidor CONFIRMA este aparelho. Antes bastava o
     navegador ter uma inscrição — e o cartão seguia "ligado" depois que o
     serviço de push cancelava a inscrição e os avisos paravam de chegar. */
  async function conferir(deNovo = false) {
    const e = await estadoNotificacao().catch(() => "indisponivel" as const);
    setEstado(e);
    setSituacao(e === "ativa" ? await sincronizarNotificacao(deNovo) : undefined);
  }

  useEffect(() => {
    conferir();
    const ouvir = () => setInstalavel(podeInstalar());
    window.addEventListener("sercamp-pode-instalar", ouvir);
    return () => window.removeEventListener("sercamp-pode-instalar", ouvir);
  }, []);

  async function ligar() {
    setOcupado(true);
    setMsg(null);
    try {
      await ativarNotificacao();
      await conferir(true);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Não foi possível ativar.");
    } finally {
      setOcupado(false);
    }
  }

  async function desligar() {
    setOcupado(true);
    await desativarNotificacao();
    await conferir();
    setOcupado(false);
  }

  async function testar() {
    setOcupado(true);
    setMsg(null);
    try {
      const r = (await api.push.testar()) as { enviados: number; aparelhos: number };
      setMsg(
        "Enviada para " + r.enviados + " de " + r.aparelhos + " aparelho(s) — deve aparecer em alguns segundos." +
          (ehComputador() ? " No computador, o navegador precisa estar aberto (pode estar minimizado)." : ""),
      );
      // O serviço confirma a entrega em instantes: atualiza "último aviso entregue".
      setTimeout(() => conferir(true), 4000);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Não foi possível testar.");
      await conferir(true);
    } finally {
      setOcupado(false);
    }
  }

  const quando = (v: string | null) =>
    v ? new Date(v).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "";
  const problema =
    situacao?.ultimo_erro &&
    (!situacao.ultimo_ok || new Date(situacao.ultimo_erro_em ?? 0) > new Date(situacao.ultimo_ok));

  return (
    <section className="ag-card">
      <h3 className="ag-card-tit">
        <IconBell size={16} /> Aviso no celular
        {estado === "ativa" && situacao && !problema && <span className="ag-card-sub ag-ligado">ligado</span>}
      </h3>
      {estado === null || (estado === "ativa" && situacao === undefined) ? (
        <p className="ag-vazio">Verificando…</p>
      ) : estado === "ativa" && situacao === null ? (
        <>
          <p className="ag-vazio">
            Este navegador está inscrito, mas o sistema não conseguiu confirmar o aparelho — os avisos podem
            não estar chegando. Toque em “Reativar”.
          </p>
          <button className="btn-primary btn-sm" disabled={ocupado} onClick={ligar}>
            <IconBell size={14} /> {ocupado ? "Reativando…" : "Reativar neste aparelho"}
          </button>
        </>
      ) : estado === "ativa" && situacao ? (
        <>
          <p className="ag-vazio">Este aparelho avisa quando chega uma solicitação nova ou um pedido de reunião, mesmo com o app fechado.</p>
          <p className="ag-vazio">
            {situacao.ultimo_ok
              ? "Último aviso entregue neste aparelho: " + quando(situacao.ultimo_ok) + "."
              : "Nenhum aviso entregue neste aparelho ainda — use “Enviar teste”."}{" "}
            {situacao.aparelhos} aparelho(s) recebem os avisos.
          </p>
          {problema && (
            <p className="ag-msg">
              Último problema ({quando(situacao.ultimo_erro_em)}): {situacao.ultimo_erro}
            </p>
          )}
          {ehComputador() && (
            <p className="ag-vazio">No computador, o aviso só aparece com o navegador aberto (pode estar minimizado).</p>
          )}
          <div className="ag-linha-botoes">
            <button className="btn-secondary btn-sm" disabled={ocupado} onClick={testar}>
              Enviar teste
            </button>
            <button className="btn-link" disabled={ocupado} onClick={desligar}>
              desligar neste aparelho
            </button>
          </div>
        </>
      ) : estado === "desligada" ? (
        <>
          <p className="ag-vazio">Receba um aviso quando chegar uma solicitação nova ou alguém pedir uma reunião. Ative em cada aparelho que você usa.</p>
          <button className="btn-primary btn-sm" disabled={ocupado} onClick={ligar}>
            <IconBell size={14} /> {ocupado ? "Ativando…" : "Ativar notificações"}
          </button>
        </>
      ) : estado === "bloqueada" ? (
        <p className="ag-vazio">
          As notificações foram bloqueadas neste navegador. Toque no cadeado ao lado do endereço →
          Notificações → Permitir, e recarregue a página.
        </p>
      ) : estado === "iphone" ? (
        <p className="ag-vazio">
          No iPhone: toque em <strong>Compartilhar</strong> → <strong>Adicionar à Tela de Início</strong>,
          abra o app por lá e ative aqui.
        </p>
      ) : (
        <p className="ag-vazio">
          Este navegador não recebe notificações. Abra o sistema pelo endereço https (no celular,
          pelo Chrome) para ativar.
        </p>
      )}
      {instalavel && (
        <button className="btn-secondary btn-sm ag-instalar" onClick={() => instalarApp()}>
          <IconDownload size={14} /> Instalar o app neste aparelho
        </button>
      )}
      {msg && <p className="ag-msg">{msg}</p>}
    </section>
  );
}

/* ==================== Horários livres e o link público ==================== */

/**
 * A AGENDA ABERTA — "marque comigo".
 *
 * Aqui você diz quando pode (por dia da semana), o que bloqueia dias
 * específicos, e as regras do encontro (duração, antecedência, até quando).
 * O link no fim é o que vai para a pessoa: ela escolhe um horário que sobrou e
 * a reunião entra na sua agenda, já com lembrete.
 */
function HorariosLivres({ onMudou, somenteLeitura = false }: { onMudou: () => void; somenteLeitura?: boolean }) {
  const [dados, setDados] = useState<any | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [faixa, setFaixa] = useState({ dia_semana: "1", hora_inicio: "09:00", hora_fim: "12:00" });
  const [bloqueio, setBloqueio] = useState({ data: "", hora_inicio: "", hora_fim: "", motivo: "" });

  const carregar = useCallback(async () => {
    try {
      setDados(await api.agenda.config());
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível carregar a agenda aberta.");
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  if (erro) return <ErrorState message={erro} onRetry={carregar} />;
  if (!dados) return <div className="carregando">Carregando…</div>;

  const cfg = dados.config;
  const link = window.location.origin + "/agendar/" + cfg.token;

  const salvarCfg = async (mudanca: Record<string, unknown>) => {
    setSalvando(true);
    try {
      await api.agenda.salvarConfig(mudanca);
      await carregar();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  };

  async function addFaixa() {
    try {
      await api.agenda.criarFaixa({
        dia_semana: Number(faixa.dia_semana),
        hora_inicio: faixa.hora_inicio,
        hora_fim: faixa.hora_fim,
      });
      await carregar();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Não foi possível adicionar.");
    }
  }

  async function addBloqueio() {
    if (!bloqueio.data) {
      alert("Escolha o dia que você quer bloquear.");
      return;
    }
    try {
      await api.agenda.criarBloqueio(bloqueio);
      setBloqueio({ data: "", hora_inicio: "", hora_fim: "", motivo: "" });
      await carregar();
      onMudou();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Não foi possível bloquear.");
    }
  }

  async function copiar() {
    try {
      await navigator.clipboard.writeText(link);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      alert("Não consegui copiar sozinho — selecione o link e copie.");
    }
  }

  function mandarNoWhats() {
    const texto =
      "Olá! Para falarmos sobre o sistema, escolha um horário que esteja livre na minha agenda:\n" +
      link;
    window.open("https://wa.me/?text=" + encodeURIComponent(texto), "_blank", "noopener");
  }

  /* Somente visualização: aqui quase tudo grava sozinho (ao sair do campo, ao
     marcar a caixa) e o esconde-por-texto de src/modo-leitura.ts só pega
     botão — o fieldset desligado trava todos os controles de uma vez. */
  return (
    <fieldset className="dev-livres" disabled={somenteLeitura}>
      {/* -------- O LINK -------- */}
      <section className="dev-bloco">
        <h3 className="section-title">
          <IconFolder size={15} /> O seu link de agendamento
        </h3>
        <p className="muted">
          Mande este endereço para quem precisa falar com você. A pessoa vê só os horários que
          sobraram, escolhe um, e a reunião entra na sua agenda com lembrete. Ela não vê nada do
          resto da sua agenda.
        </p>
        <div className="triagem-link-linha">
          <input readOnly value={link} onFocus={(e) => e.target.select()} />
          <button className="btn-secondary btn-sm" onClick={copiar}>
            {copiado ? <IconCheck size={15} /> : <IconFolder size={15} />}{" "}
            {copiado ? "Copiado!" : "Copiar"}
          </button>
          <button className="btn-whats btn-sm" onClick={mandarNoWhats}>
            Enviar no WhatsApp
          </button>
        </div>
        <div className="dev-livres-linha">
          <label className="dev-check">
            <input
              type="checkbox"
              checked={cfg.ativa}
              disabled={salvando}
              onChange={(e) => salvarCfg({ ativa: e.target.checked })}
            />
            Agenda aberta (desmarque para tirar o link do ar)
          </label>
          <button
            className="btn-link"
            onClick={async () => {
              if (!confirm("Gerar um link novo? O atual para de funcionar na hora.")) return;
              const r = await api.agenda.novoLink();
              alert(r?.mensagem ?? "Link novo gerado.");
              await carregar();
            }}
          >
            gerar link novo
          </button>
          <span className="muted">
            {dados.totalHorariosLivres} horário(s) livre(s) nos próximos {cfg.janela_dias} dias
          </span>
        </div>
      </section>

      {/* -------- AS REGRAS -------- */}
      <section className="dev-bloco">
        <h3 className="section-title">
          <IconCalendarCheck size={15} /> Como as pessoas podem marcar
        </h3>
        <div className="form-grid">
          <label className="field field-wide">
            <span>Título que a pessoa vê</span>
            <input
              defaultValue={cfg.titulo}
              onBlur={(e) => e.target.value !== cfg.titulo && salvarCfg({ titulo: e.target.value })}
            />
          </label>
          <label className="field field-wide">
            <span>Recado na página (opcional)</span>
            <textarea
              rows={2}
              defaultValue={cfg.instrucao ?? ""}
              placeholder="Ex.: escolha um horário e me conte no campo da pauta o que precisa."
              onBlur={(e) =>
                e.target.value !== (cfg.instrucao ?? "") && salvarCfg({ instrucao: e.target.value })
              }
            />
          </label>
          <label className="field">
            <span>Duração de cada reunião (min)</span>
            <input
              type="number"
              min={10}
              step={5}
              defaultValue={cfg.duracao_min}
              onBlur={(e) => salvarCfg({ duracao_min: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Respiro entre reuniões (min)</span>
            <input
              type="number"
              min={0}
              step={5}
              defaultValue={cfg.intervalo_min}
              onBlur={(e) => salvarCfg({ intervalo_min: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Antecedência mínima (horas)</span>
            <input
              type="number"
              min={0}
              defaultValue={cfg.antecedencia_h}
              onBlur={(e) => salvarCfg({ antecedencia_h: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Agenda aberta por (dias)</span>
            <input
              type="number"
              min={1}
              max={180}
              defaultValue={cfg.janela_dias}
              onBlur={(e) => salvarCfg({ janela_dias: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Local padrão</span>
            <input
              defaultValue={cfg.local_padrao ?? ""}
              placeholder="Ex.: sala do administrativo"
              onBlur={(e) => salvarCfg({ local_padrao: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Link padrão da chamada</span>
            <input
              defaultValue={cfg.link_padrao ?? ""}
              placeholder="https://meet…"
              onBlur={(e) => salvarCfg({ link_padrao: e.target.value })}
            />
          </label>
        </div>
        <p className="hint">As mudanças são salvas quando você sai do campo.</p>
      </section>

      {/* -------- FAIXAS LIVRES POR DIA DA SEMANA -------- */}
      <section className="dev-bloco">
        <h3 className="section-title">
          <IconClock size={15} /> Quando você está livre
        </h3>
        <p className="muted">
          Faixas que se repetem toda semana. Só é oferecido o que estiver dentro delas — e que não
          esteja ocupado nem bloqueado.
        </p>
        <div className="dev-faixa-form">
          <select
            value={faixa.dia_semana}
            onChange={(e) => setFaixa({ ...faixa, dia_semana: e.target.value })}
          >
            {DIAS_SEMANA.map((d, i) => (
              <option key={d} value={i}>
                {d}
              </option>
            ))}
          </select>
          <input
            type="time"
            value={faixa.hora_inicio}
            onChange={(e) => setFaixa({ ...faixa, hora_inicio: e.target.value })}
          />
          <span className="muted">até</span>
          <input
            type="time"
            value={faixa.hora_fim}
            onChange={(e) => setFaixa({ ...faixa, hora_fim: e.target.value })}
          />
          <button className="btn-primary btn-sm" onClick={addFaixa}>
            <IconPlus size={15} /> Adicionar
          </button>
        </div>

        {dados.disponibilidade.length === 0 ? (
          <p className="muted">
            Nenhuma faixa cadastrada — enquanto isso, o link não oferece horário nenhum.
          </p>
        ) : (
          <ul className="dev-lista">
            {dados.disponibilidade.map((f: any) => (
              <li key={f.id}>
                <span className="pill pill-ok">{DIAS_SEMANA[f.dia_semana]}</span>
                <span className="dev-lista-main">
                  <strong>
                    {f.hora_inicio} às {f.hora_fim}
                  </strong>
                  <small className="muted">
                    cabem {Math.floor(
                      ((Number(f.hora_fim.slice(0, 2)) * 60 + Number(f.hora_fim.slice(3))) -
                        (Number(f.hora_inicio.slice(0, 2)) * 60 + Number(f.hora_inicio.slice(3)))) /
                        Math.max(10, cfg.duracao_min + cfg.intervalo_min),
                    )}{" "}
                    reunião(ões) de {cfg.duracao_min} min
                  </small>
                </span>
                <button
                  className="icon-btn icon-btn-danger"
                  title="Remover faixa"
                  onClick={async () => {
                    await api.agenda.removerFaixa(f.id);
                    await carregar();
                  }}
                >
                  <IconTrash size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* -------- BLOQUEIOS -------- */}
      <section className="dev-bloco">
        <h3 className="section-title">
          <IconLock size={15} /> Dias e horários bloqueados
        </h3>
        <p className="muted">
          A exceção ganha da faixa: feriado, viagem, uma tarde ocupada. Sem as horas, o dia inteiro
          fica bloqueado.
        </p>
        <div className="dev-faixa-form">
          <input
            type="date"
            value={bloqueio.data}
            onChange={(e) => setBloqueio({ ...bloqueio, data: e.target.value })}
          />
          <input
            type="time"
            value={bloqueio.hora_inicio}
            onChange={(e) => setBloqueio({ ...bloqueio, hora_inicio: e.target.value })}
          />
          <span className="muted">até</span>
          <input
            type="time"
            value={bloqueio.hora_fim}
            onChange={(e) => setBloqueio({ ...bloqueio, hora_fim: e.target.value })}
          />
          <input
            placeholder="Motivo (opcional)"
            value={bloqueio.motivo}
            onChange={(e) => setBloqueio({ ...bloqueio, motivo: e.target.value })}
          />
          <button className="btn-primary btn-sm" onClick={addBloqueio}>
            <IconPlus size={15} /> Bloquear
          </button>
        </div>

        {dados.bloqueios.length === 0 ? (
          <p className="muted">Nenhum bloqueio.</p>
        ) : (
          <ul className="dev-lista">
            {dados.bloqueios.map((b: any) => (
              <li key={b.id}>
                <span className="pill pill-danger">{dataBr(b.data)}</span>
                <span className="dev-lista-main">
                  <strong>
                    {b.hora_inicio && b.hora_fim
                      ? b.hora_inicio + " às " + b.hora_fim
                      : "dia inteiro"}
                  </strong>
                  <small className="muted">{b.motivo || "sem motivo anotado"}</small>
                </span>
                <button
                  className="icon-btn icon-btn-danger"
                  title="Liberar"
                  onClick={async () => {
                    await api.agenda.removerBloqueio(b.id);
                    await carregar();
                  }}
                >
                  <IconTrash size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* -------- PRÉVIA -------- */}
      <section className="dev-bloco">
        <h3 className="section-title">
          <IconEye size={15} /> O que a pessoa vai ver
        </h3>
        {dados.proximosLivres.length === 0 ? (
          <p className="muted">
            Nenhum horário disponível com as regras atuais. Confira as faixas, os bloqueios e a
            antecedência mínima.
          </p>
        ) : (
          <ul className="dev-lista">
            {dados.proximosLivres.map((d: any) => (
              <li key={d.data}>
                <span className="pill pill-neutral">{dataBr(d.data)}</span>
                <span className="dev-lista-main">
                  <span className="dev-slots">
                    {d.horarios.map((h: string) => (
                      <span className="dev-slot" key={h}>
                        {h}
                      </span>
                    ))}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </fieldset>
  );
}
